/**
 * SettingsLanguages template component.
 */
import Bold from '@components/atoms/text/bold';
import Container from '@components/atoms/container/languagesContainer';
import FlavourConfig from '@shared/config/flavours';
import Focal from '@components/atoms/text/focal';
import LanguageInfoBlock from '@components/atoms/container/languageInfoBlock';
import Focus from '@shared/types/enums/focus';
import Languages from '@shared/types/enums/languages';
import LanguageContext from '@shared/components/utils/context/languageContext';
import LanguageInfo from '@components/atoms/text/languageInfo';
import LanguagesList from '@components/sections/list/languagesList/languagesList';
import TranslationManager from '@shared/assets/locale/translationsManager';
import Viewport from '@components/atoms/container/contentContainer';
import useTranslationRefresh from '@shared/components/utils/hooks/useTranslationRefresh';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { setFocus, focusSelector } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef, useState } from 'react';

/**
 * Constants
 */
const LITERALS = 'PAGES.SETTINGS.LANGUAGES';

/**
 * Displays a list of languages to choose from.
 */
export default function LanguageSettingsTemplate() {
  const { displayLanguage, setLanguage, text } = useLanguageSettingsTemplate();
  return (
    <LanguageContext.Provider value={{ displayLanguage, setLanguage }}>
      <Viewport id={'languageContainer'}>
        <Container>
          <LanguagesList animate={false} languages={FlavourConfig.languages} />
          <LanguageInfo id={'textLanguageInfo'}>
            <LanguageInfoBlock>
              <Bold id={'titleLanguageInfo'}>{text.title}</Bold>
              <Focal id={'selectionLanguageInfo'}>{text.selection}</Focal>
            </LanguageInfoBlock>
            <LanguageInfoBlock>{text.description}</LanguageInfoBlock>
            <LanguageInfoBlock>{text.action}</LanguageInfoBlock>
          </LanguageInfo>
        </Container>
      </Viewport>
    </LanguageContext.Provider>
  );
}

/**
 * Hook to use the language settings template.
 * Handles the language settings template logic.
 */
function useLanguageSettingsTemplate() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { focus } = useAppSelector(focusSelector);

  // State
  const [text, setText] = useState({
    action: '',
    description: '',
    selection: '',
    title: '',
  });

  // Refs
  const currentLanguage = useRef(
    TranslationManager.currentLanguage as Languages
  );

  // ------------------------------------------------------------
  // Translation hook
  // ------------------------------------------------------------

  useTranslationRefresh();

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const returnAction = () => {
    dispatch(popRoute());
  };

  const displayLanguage = (language: Languages) => {
    TranslationManager.setLanguage(language);
    setText({
      action: TranslationManager.get(
        `${LITERALS}.ACTION`,
        FlavourConfig.appTitle
      ),
      description: TranslationManager.get(`${LITERALS}.SELECT_LANGUAGE`),
      selection: TranslationManager.get(
        `${LITERALS}.LANGUAGE_SELECTED`,
        TranslationManager.get(
          `MISC.LANGUAGES.TRANSLATED.${currentLanguage.current}`
        )
      ),
      title: TranslationManager.get(`${LITERALS}.CONFIG`),
    });
  };

  const setLanguage = () => {
    currentLanguage.current = TranslationManager.currentLanguage;
    returnAction();
  };

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    displayLanguage(currentLanguage.current);
    dispatch(setFocus(Focus.LANGUAGES));
    return () => {
      TranslationManager.setLanguage(currentLanguage.current);
    };
  }, []);

  useEffect(() => {
    if (focus !== Focus.LANGUAGES) {
      TranslationManager.setLanguage(currentLanguage.current);
    }
  }, [focus]);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.LANGUAGES,
    keyDownHandler: (key) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
        case 'SELECT':
          setLanguage();
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.LANGUAGES));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Return values
  // ------------------------------------------------------------

  return {
    displayLanguage,
    setLanguage,
    text,
  };
}
